import React, { useEffect } from 'react';
import { ArrowDown, ArrowRight, Wrench, ImageIcon, Timer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
const HeroSection = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('opacity-100', 'translate-y-0');
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '50px'
    });
    const elements = document.querySelectorAll('.hero-animate');
    elements.forEach(el => observer.observe(el));
    return () => {
      elements.forEach(el => observer.unobserve(el));
    };
  }, []);
  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing');
    if (pricingSection) {
      pricingSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };

  // Reduced number of bubbles for better performance
  const bubbles = Array.from({
    length: 5 // Reduced from 10 to 5
  }).map((_, i) => ({
    id: i,
    size: Math.floor(Math.random() * 30) + 20,
    // Reduced max size
    left: `${Math.floor(Math.random() * 100)}%`,
    top: `${Math.floor(Math.random() * 100)}%`,
    duration: Math.floor(Math.random() * 15) + 10,
    delay: Math.floor(Math.random() * 3) // Reduced delay
  }));
  return <section className="relative min-h-screen pt-24 px-4 flex flex-col justify-center items-center bg-gradient-to-br from-white to-blue-50 overflow-hidden">
      {/* Background Floating Bubbles - Reduced for performance */}
      {bubbles.map(bubble => <div key={bubble.id} className="absolute rounded-full opacity-20 animate-float" style={{
      width: `${bubble.size}px`,
      height: `${bubble.size}px`,
      left: bubble.left,
      top: bubble.top,
      background: 'rgba(240, 58, 58, 0.5)',
      animationDuration: `${bubble.duration}s`,
      animationDelay: `${bubble.delay}s`
    }} />)}
      
      {/* Content Container */}
      <div className="container mx-auto max-w-6xl z-10 flex flex-col items-center">
        {/* Lab Flask Icon */}
        <div className="relative mb-8 hero-animate opacity-0 translate-y-4 transition-all duration-700">
          <div className="animate-float">
            <img src="/lovable-uploads/9660a1c7-e321-4122-b380-58c44ed97180.png" alt="AD Email Lab" className="w-32 h-32 md:w-40 md:h-40 object-contain" width="160" height="160" loading="eager" />
          </div>
        </div>

        {/* Main Headline */}
        <h1 className="text-4xl sm:text-5xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-center mb-6 hero-animate opacity-0 translate-y-4 transition-all duration-700 delay-100">
          <span className="text-secondary">Building Digital Experiences </span>
          <span className="text-primary">That Convert</span>
        </h1>

        {/* Subheadline */}
        <div className="text-lg sm:text-xl md:text-xl lg:text-xl xl:text-2xl text-center text-gray-600 mb-10 max-w-5xl hero-animate opacity-0 translate-y-4 transition-all duration-700 delay-200 space-y-6">
          <p>
            <strong>AD Email Lab</strong> is a collection of practical tools, insightful articles, and digital marketing resources built from real-world experience. Covering <strong>CRM</strong>, <strong>email marketing</strong>, <strong>web development</strong>, <strong>marketing automation</strong>, and <strong>digital marketing</strong>, the goal is simple: help professionals work smarter, create better experiences, and drive meaningful results.
          </p>
          <p>
            Created by <strong>Antonio Dimoski</strong>, a CRM and Digital Marketing Specialist with experience across Salesforce Marketing Cloud, Braze, Klaviyo, Adobe Experience Platform, web development, and paid advertising — AD Email Lab brings together the knowledge, tools, and solutions I use every day to help businesses grow through technology, automation, and creativity.
          </p>
        </div>

        {/* CTA Button */}
        
        
        {/* Tools Section */}
        <div className="mb-8 max-w-5xl mx-auto hero-animate opacity-0 translate-y-4 transition-all duration-700 delay-400 py-[10px]">
          <h2 className="text-2xl font-bold text-center mb-8 text-secondary">
            Try my AD Email Builder, Image Editor, <span className="text-primary">or</span> Email Countdown Generator
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            {/* Email Builder */}
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 group hover:shadow-xl transition-all duration-300 flex flex-col h-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <Wrench className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-secondary">Email Builder</h3>
              </div>
              <p className="text-gray-600 mb-6 leading-relaxed flex-grow">
                Create stunning, responsive email templates with our intuitive drag-and-drop builder. Perfect for email marketing campaigns that convert.
              </p>
              <Button onClick={() => window.open('https://ad-email-lab-builder.lovable.app/', '_blank')} className="w-full bg-primary hover:bg-primary/90 text-white">
                Launch Email Builder
              </Button>
            </div>
            
            {/* Image Editor */}
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 group hover:shadow-xl transition-all duration-300 flex flex-col h-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-green-50 rounded-lg">
                  <ImageIcon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-secondary">Image Editor</h3>
              </div>
              <p className="text-gray-600 mb-6 leading-relaxed flex-grow">
                Simple and professional image editing for email marketing. Crop, resize, and optimize images for perfect email campaigns.
              </p>
              <Button onClick={() => window.open('https://ad-email-image-editor.lovable.app/', '_blank')} className="w-full bg-primary hover:bg-primary/90 text-white">
                Launch Image Editor
              </Button>
            </div>
            
            {/* Email Countdown Generator */}
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 group hover:shadow-xl transition-all duration-300 flex flex-col h-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-orange-50 rounded-lg">
                  <Timer className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-secondary">Email Countdown Generator</h3>
              </div>
              <p className="text-gray-600 mb-6 leading-relaxed flex-grow">
                Build animated countdown timers for your emails. Boost urgency and engagement with easy-to-generate countdown GIFs.
              </p>
              <Button onClick={() => window.open('https://email-timer-craft.lovable.app/', '_blank')} className="w-full bg-primary hover:bg-primary/90 text-white">
                Launch Countdown Generator
              </Button>
            </div>
          </div>
        </div>

        {/* New animated text highlighting additional services - With improved layout, arrow icon, and matching button color */}
        <p className="text-base mb-16 max-w-2xl text-center hero-animate opacity-0 translate-y-4 transition-all duration-700 delay-500">
          <span className="relative inline-block after:content-[''] after:absolute after:w-full after:h-0.5 after:bottom-0 after:left-0 after:bg-primary after:origin-bottom-right after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-500 hover:after:origin-bottom-left">
            <span className="text-primary font-bold flex flex-col sm:flex-row items-center justify-center gap-2">
              <span>Beyond email development</span>
              <ArrowRight className="h-5 w-5 text-primary hidden sm:inline-block" />
              <span className="text-primary font-bold">providing comprehensive digital marketing services</span>
            </span>
            <span className="block mt-1 text-gray-600">including <strong>web design</strong>, <strong>branding</strong>, <strong>ad campaigns</strong>, and <strong>strategic business growth consulting</strong>.</span>
          </span>
        </p>

        {/* Animated Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 scroll-indicator">
          <ArrowDown className="h-8 w-8 text-primary/70" />
        </div>

        {/* Animated Dashboard Preview - optimized with reduced details */}
        <div className="relative w-full max-w-4xl mx-auto mt-4 hero-animate opacity-0 translate-y-4 transition-all duration-700 delay-400">
          <div className="bg-white rounded-xl shadow-2xl p-2 md:p-4 border border-gray-200 animate-custom-float">
            <div className="bg-gradient-to-r from-secondary to-primary rounded-lg h-10 mb-3 flex items-center px-4">
              <div className="flex space-x-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
              </div>
            </div>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-3 hidden lg:block">
                <div className="bg-gray-100 rounded-lg h-12 mb-4"></div>
                <div className="bg-gray-100 rounded-lg h-8 mb-3"></div>
                <div className="bg-gray-100 rounded-lg h-8 mb-3"></div>
                <div className="bg-gray-100 rounded-lg h-8 mb-3"></div>
                <div className="bg-primary/20 rounded-lg h-8 mb-3"></div>
                <div className="bg-gray-100 rounded-lg h-8"></div>
              </div>
              <div className="col-span-12 lg:col-span-9">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-secondary/10 rounded-lg h-20 md:h-24 animate-pulse-light"></div>
                  <div className="bg-primary/10 rounded-lg h-20 md:h-24 animate-pulse-light"></div>
                </div>
                <div className="bg-gray-100 h-32 md:h-40 rounded-lg mb-4"></div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-100 h-12 md:h-16 rounded-lg"></div>
                  <div className="bg-primary/20 h-12 md:h-16 rounded-lg"></div>
                  <div className="bg-gray-100 h-12 md:h-16 rounded-lg"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default HeroSection;